import { ZERO_WEI } from '@kwenta/sdk/constants'
import type { TokenTickersAndEth } from '@kwenta/sdk/types'
import { formatNumber, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import AlertIcon from 'assets/svg/app/alert-triangle.svg'
import CloseIcon from 'assets/svg/app/close.svg'
import AddressBook from 'components/AddressBook'
import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import DropdownField from 'components/DropdownField'
import NumericInput from 'components/Input/NumericInput'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { DEFAULT_TOKEN_DECIMALS, DEFAULT_USDC_DECIMALS } from 'constants/defaults'
import isEqual from 'lodash/isEqual'
import { type FC, memo, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { getProviderForCoinAndChain } from 'sections/accounts/Deposits/DepositInfo'
import { DrawerTab, WithdrawTab, chainOptions, coinOptions } from 'sections/accounts/constants'
import { SectionHeading } from 'sections/shared/components/SectionHeading'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { selectShowModalWithParams } from 'state/app/selectors'
import { selectBalancesAllProviders } from 'state/balances/selectors'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import styled from 'styled-components'
import { resetButtonCSS } from 'styles/common'
import { isAddress } from 'viem'
import { TransferMargin } from './TransferMarginPanel'

type WithdrawPanelProps = {
	onDismiss: () => void
}

const WithdrawPanel: FC<WithdrawPanelProps> = memo(({ onDismiss }) => {
	const modalPrams = useAppSelector(selectShowModalWithParams)?.params
	const innerTab = modalPrams?.innerTab ?? WithdrawTab.ONCHAIN
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const dispatch = useAppDispatch()
	const { t } = useTranslation()

	const switchTab = useCallback(
		(newTab: WithdrawTab) => {
			dispatch(
				setOpenModalWithParams({
					type: 'manage_wallet',
					params: { tab: DrawerTab.WITHDRAW, innerTab: newTab },
				})
			)
		},
		[dispatch]
	)

	const withdrawTabs = useMemo(
		() => [
			{
				name: WithdrawTab.ONCHAIN,
				label: t('account.withdraw-section.tabs.onchain-withdrawal'),
				active: innerTab === WithdrawTab.ONCHAIN,
				onClick: (): void => switchTab(WithdrawTab.ONCHAIN),
				hide: !abstractionWalletEnabled,
			},
			{
				name: WithdrawTab.INTERNAL,
				label: t('account.withdraw-section.tabs.internal-transfer'),
				active: innerTab === WithdrawTab.INTERNAL,
				onClick: (): void => switchTab(WithdrawTab.INTERNAL),
			},
		],
		[t, innerTab, abstractionWalletEnabled, switchTab]
	)

	return (
		<Container>
			<PanelContainer rowGap="15px">
				<FlexDivRowCentered>
					<SectionHeading
						title={t(
							`account.withdraw-section.title-${innerTab === WithdrawTab.ONCHAIN ? 'withdraw' : 'transfer'}`
						)}
						marginTop={0}
					/>
					<DismissButton onClick={onDismiss}>
						<CloseIcon width={12} height={12} />
					</DismissButton>
				</FlexDivRowCentered>
				<TabButtonsContainer>
					<UnderlineTabs options={withdrawTabs.filter((t) => !t.hide)} noBorders={true} />
				</TabButtonsContainer>
				{innerTab === WithdrawTab.ONCHAIN && <Withdraw />}
				{innerTab === WithdrawTab.INTERNAL && <TransferMargin />}
			</PanelContainer>
		</Container>
	)
})

const Withdraw: FC = memo(() => {
	const { t } = useTranslation()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const dispatch = useAppDispatch()

	const [amount, setAmount] = useState('')
	const [withdrawAddress, setWithdrawAddress] = useState('')
	const walletBalances = useAppSelector(selectBalancesAllProviders, isEqual)
	const coinsList = useMemo(
		() => coinOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)
	const chainsList = useMemo(
		() => chainOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)

	const [selectedCoinValue, setSelectedCoinValue] = useState(coinsList[0]?.value)
	const [selectedChainValue, setSelectedChainValue] = useState(chainsList[0]?.value)
	const [selectedChainId, setSelectedChainId] = useState(chainsList[0]?.id)

	const totalWithdrawable = useMemo(
		() =>
			wei(
				walletBalances?.[selectedChainId]?.[selectedCoinValue.toUpperCase() as TokenTickersAndEth]
					?.balance ?? ZERO_WEI
			),
		[walletBalances, selectedChainId, selectedCoinValue]
	)

	const handleSetMax = useCallback(() => {
		setAmount(
			truncateNumbers(
				totalWithdrawable,
				selectedCoinValue === 'ETH' ? DEFAULT_TOKEN_DECIMALS : DEFAULT_USDC_DECIMALS
			)
		)
	}, [totalWithdrawable, selectedCoinValue])

	const handleCoinChange = (newCoin: string) => {
		setSelectedCoinValue(newCoin)

		const provider = getProviderForCoinAndChain(newCoin, selectedChainValue)

		if (!provider) {
			const coinProviders = coinOptions.find((c) => c.value === newCoin)?.providers || []
			const availableChains = chainOptions.filter((c) =>
				c.providers.some((p) => coinProviders.includes(p))
			)

			if (availableChains.length > 0) {
				setSelectedChainValue(availableChains[0].value)
				setSelectedChainId(availableChains[0].id)
			}
		}
	}

	const handleChainChange = (newChain: string) => {
		setSelectedChainValue(newChain)

		const selectedChain = chainsList.find((chain) => chain.value === newChain)
		if (selectedChain) {
			setSelectedChainId(selectedChain.id)
		}

		const provider = getProviderForCoinAndChain(selectedCoinValue, newChain)

		if (!provider) {
			const chainProviders = chainOptions.find((c) => c.value === newChain)?.providers || []
			const availableCoins = coinOptions.filter((c) =>
				c.providers.some((p) => chainProviders.includes(p))
			)

			if (availableCoins.length > 0) {
				setSelectedCoinValue(availableCoins[0].value)
			}
		}
	}

	const showWalletDrawer = useCallback(() => dispatch(setOpenModal('manage_wallet')), [dispatch])

	return (
		<FlexDivCol rowGap="25px">
			<FlexDivCol rowGap="20px">
				<DropdownField
					label={t('account.withdraw-section.coin')}
					options={coinOptions}
					value={selectedCoinValue}
					onChange={handleCoinChange}
					background="hover"
				/>
				<DropdownField
					label={t('account.withdraw-section.chain')}
					options={chainOptions}
					value={selectedChainValue}
					onChange={handleChainChange}
					background="hover"
				/>
				<ImportantMessageContainer columnGap="4px">
					<InfoIconContainer>
						<AlertIcon width={16} height={16} />
					</InfoIconContainer>
					<Body size="small" color="secondary" style={{ lineHeight: '18px' }}>
						<Paragraph color="secondary">{t('account.withdraw-section.copy')}</Paragraph>
					</Body>
				</ImportantMessageContainer>
				<AddressBook
					withdrawAddress={withdrawAddress}
					onWithdrawAddressChange={(value) => setWithdrawAddress(value)}
				/>
				<FlexDivCol rowGap="10px">
					<FlexDivRow>
						<Body color="secondary">{t('account.withdraw-section.amount')}</Body>
						<ClickableBody color="secondary" onClick={handleSetMax}>
							{t('account.withdraw-section.balance')}{' '}
							{formatNumber(totalWithdrawable, { suggestDecimals: true, floor: true })}
						</ClickableBody>
					</FlexDivRow>
					<StyledNumericInput placeholder="0.00" value={amount} onChange={(_, v) => setAmount(v)} />
					{totalWithdrawable.eq(0) && (
						<MessageContainer>
							<Body size="small" color="secondary" style={{ lineHeight: '18px' }}>
								<Trans
									i18nKey={t('account.withdraw-section.transfer-message')}
									components={[
										<Emphasis
											key="emphasis"
											onClick={() =>
												dispatch(
													setOpenModalWithParams({
														type: 'manage_wallet',
														params: { tab: DrawerTab.WITHDRAW, innerTab: WithdrawTab.INTERNAL },
													})
												)
											}
										/>,
									]}
								/>
							</Body>
						</MessageContainer>
					)}
				</FlexDivCol>
				<Divider height={1} divider />
				<FlexDivCol rowGap="10px">
					<FlexDivRowCentered>
						<Body color="secondary">{t('account.withdraw-section.withdraw-account')}</Body>
						<Body color="secondary">{t('account.withdraw-section.snx-account')}</Body>
					</FlexDivRowCentered>
					<FlexDivRowCentered>
						<Body color="secondary">{t('account.withdraw-section.network-fee')}</Body>
						<Body color="secondary">
							{formatNumber(ZERO_WEI)} {selectedCoinValue}
						</Body>
					</FlexDivRowCentered>
					<FlexDivRowCentered>
						<Body color="secondary">{t('account.withdraw-section.amount-received')}</Body>
						<Body color="primary" size="large" weight="bold">
							{amount === '' ? formatNumber(ZERO_WEI) : amount} {selectedCoinValue}
						</Body>
					</FlexDivRowCentered>
				</FlexDivCol>
			</FlexDivCol>
			<Footer>
				<Button size="small" fullWidth onClick={showWalletDrawer}>
					{t('account.withdraw-section.cancel-button')}
				</Button>
				<Button
					size="small"
					variant="solid-highlight"
					fullWidth
					disabled={
						amount === '' ||
						wei(amount).gt(totalWithdrawable) ||
						wei(totalWithdrawable).eq(ZERO_WEI) ||
						!isAddress(withdrawAddress)
					}
					onClick={() =>
						dispatch(
							setOpenModalWithParams({
								type: 'manage_wallet',
								params: {
									tab: DrawerTab.CONFIRM,
									amount,
									withdrawAddress,
									selectedChain: selectedChainId,
									selectedCoin: selectedCoinValue,
								},
							})
						)
					}
				>
					{t('account.withdraw-section.withdraw-button')}
				</Button>
			</Footer>
		</FlexDivCol>
	)
})

const ClickableBody = styled(Body)`
	cursor: pointer;
`

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const Paragraph = styled(Body)`
	line-height: 18px;
`

const InfoIconContainer = styled.div`
	width: 5%;
	margin-top: 2px;
`

const PanelContainer = styled(FlexDivCol)`
	padding: 20px;
	margin-bottom: 60px;
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 25px;
	right: 20px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const Footer = styled(FlexDivRowCentered)`
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	column-gap: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const StyledNumericInput = styled(NumericInput)`
	width: 100%;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
	height: 46px;
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;

	button {
		font-size: 13px;
	}

	> div > div {
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const Divider = styled(Spacer)`
	width: 100%;
`

const ImportantMessageContainer = styled(FlexDivRow)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.highlight.background};
	flex: none;
	order: 0;
	flex-grow: 0;
	text-align: left;
	font-size: 12px;
	padding: 10px 12px;
	border-radius: 4px;
	cursor: default;
	white-space: pre-line;
`

const MessageContainer = styled.div`
	text-align: left;
	padding: 10px 12px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.04);
`

const Emphasis = styled.span`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.sectionHeader};
	font-weight: 600;
	cursor: pointer;
`

export default WithdrawPanel
